import React from 'react'
import Link from '../Link/index'
import PlaylistAddRemoveGraphql from '../PlaylistAddRemove/PlaylistAddRemoveGraphql/index'
import { UI_LOCATION } from '../../services/event-tracking/index'
import { makeIcon } from '../OracleCommons/icons'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'

export interface RelatedDocument {
  contentId: number // Content.id and Content.contentId are different fields!
  thumb: string // Fully qualified URL, probably to Brooklyn
  title: string // For hover tooltip
  path: string // The URL to follow on click
  contentType: string // e.g. 'video'
  parentSeriesName?: string | null // Optional field for event tracking
  parentSeriesId?: number | null // Optional field for event tracking
  mediaId?: number | null // Optional field for event tracking
  mediaLanguage?: string | null // Optional field for event tracking
  isFeaturedMedia?: boolean // Optional field for event tracking
}

interface MediaTileProps {
  document: RelatedDocument
  onClick?: () => void // Optional field for event tracking
}

/**
 * MediaTile - Displays a clickable video thumbnail for Gaia content
 *
 * Features:
 * - 16:9 aspect ratio (142px × 80px default)
 * - Scales up 25% on hover (~178px × 100px)
 * - Uses Link component for internal Gaia navigation
 * - Shows centered play icon as visual indicator
 */
export const MediaTile: React.FC<MediaTileProps> = ({ document, onClick }) => {
  const tipText = useTranslatedText({ keys: ['addToPlaylist', 'tipText'] })

  return (
    <div className="media-tile">
      <Link
        to={`${document.path}?fullplayer=feature`}
        className="media-tile__image-wrapper"
        title={document.title}
        target="_blank"
        directLink
        onClick={onClick}
      >
        <img
          src={document.thumb}
          alt={document.title}
          className="media-tile__image"
          loading="lazy"
        />
        <div className="media-tile__play-icon">
          {makeIcon('play', '24px')}
        </div>
      </Link>
      <div className="media-tile__action-buttons">
        <PlaylistAddRemoveGraphql
          className="media-tile__add-to-playlist-button"
          contentId={document.contentId}
          contentTitle={document.title}
          contentType={document.contentType}
          buttonSvgIcon={makeIcon('dots-vertical', '24px', { color: 'var(--oracle-w100)' })}
          ga4EventData={{
            content_id: document.contentId,
            content_type: document.contentType,
            content_name: document.title,
            media_id: document.mediaId,
            media_language: document.mediaLanguage,
            is_featured_media: true,
            parent_series_name: document.parentSeriesName,
            parent_series_id: document.parentSeriesId,
            ui_location: UI_LOCATION.AI_CHAT,
            ui_element: 'AI Chat Tile',
          }}
          tipText={tipText}
        />
      </div>
    </div>
  )
}
